import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants/networking.constants';
import {
  AlertSeverityEnum,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import {
  CitiesDataPagingRes,
  City,
} from '@/shared/interfaces/erp/system-settings';
import { useUiStore } from '@/store/ui';

const { get, post, put, remove } = erpApi();

export const useFetchCitiesEnabled = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<getCitiesParams>) => {
  return useQuery({
    queryKey: ['cities', ...Object.values(params ?? {})],
    queryFn: async () => {
      return getCities(params);
    },
    enabled,
  });
};

export const useGetCity = (id: string) => {
  return useQuery({
    queryKey: ['cities', id],
    queryFn: async () => {
      return getCity(id);
    },
  });
};

export const useCreateCity = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createCity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cities'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Ciudad creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateCity = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateCity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cities'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Ciudad creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useRemoveCity = () => {
  const queryClient = useQueryClient();
  const setConfirmDialogIsOpen = useUiStore.getState().setConfirmDialogIsOpen;

  return useMutation({
    mutationFn: removeCity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cities'] });
    },
    onSettled: () => {
      setConfirmDialogIsOpen(false);
    },
  });
};

///* axios
export type getCitiesParams = Partial<City> & {
  page?: number;
  page_size?: number;

  filterByState?: boolean;

  pais_codigo?: string;
  provincia_codigo?: string;
};

export type CreateCityParams = Omit<City, 'id_ciudad'>;
export type UpdateCityParams = {
  id: string;
  data: CreateCityParams;
};

export const getCities = async (params?: getCitiesParams) => {


  const queryParams = getUrlParamsFetch(params || {});

  return get<CitiesDataPagingRes>(
    `${urlBackend}api/v1/ciudad/?${queryParams}`,
    true
  );
};

export const getCity = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<City>(`${urlBackend}api/v1/ciudad/${id}/`, true);

  res.status === 'error' && setAlert(true, res.message, 'error');
  return res;
};

export const getCityByProvinces = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<City>(`${urlBackend}api/v1/ciudad/?=${id}/`, true);

  res.status === 'error' && setAlert(true, res.message, 'error');
  return res;
};

export const createCity = async (data: CreateCityParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<City>(`${urlBackend}api/v1/ciudad/`, data, true);
};

export const updateCity = async ({ id, data }: UpdateCityParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<City>(`${urlBackend}api/v1/ciudad/${id}/`, data, true);
};

export const removeCity = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await remove<City>(`${urlBackend}api/v1/ciudad/${id}/`, true);

  setAlert(true, res.message, res.status);
  return res;
};
